import { createSelector } from '@reduxjs/toolkit';

import type { RootState } from '@/store';

import type {
  BotFlowsElegiveisPages,
  BotFlowsElegiveisPagesQuery,
  BotFlowsElegiveisQuery,
} from '../../types';
import { botFlowsElegiveisQueryKey } from '../../utils/botFlowsElegiveis';

export const selectConversationsState = (state: RootState) => state.chatAtendimentoConversations;
export const selectConversationsItems = (state: RootState) =>
  state.chatAtendimentoConversations.items;
export const selectConversationsTotal = (state: RootState) =>
  state.chatAtendimentoConversations.total;
export const selectIsLoadingConversations = (state: RootState) =>
  state.chatAtendimentoConversations.isLoadingList;
export const selectConversationsError = (state: RootState) =>
  state.chatAtendimentoConversations.errorList;
export const selectSelectedConversationId = (state: RootState) =>
  state.chatAtendimentoConversations.selectedId;
export const selectSelectedConversationDetails = (state: RootState) =>
  state.chatAtendimentoConversations.selectedDetails;
export const selectIsLoadingConversationDetails = (state: RootState) =>
  state.chatAtendimentoConversations.isLoadingDetails;
export const selectConversationsHasMore = (state: RootState) =>
  state.chatAtendimentoConversations.hasMore;
export const selectConversationsNextCursor = (state: RootState) =>
  state.chatAtendimentoConversations.nextCursor;
export const selectIsLoadingMore = (state: RootState) =>
  state.chatAtendimentoConversations.isLoadingMore;

export const selectSelectedConversation = createSelector(
  [selectConversationsItems, selectSelectedConversationId],
  (items, selectedId) => (selectedId ? (items.find((c) => c.id === selectedId) ?? null) : null),
);

export const selectLinhas = (state: RootState) => state.chatAtendimentoConversations.linhas;
export const selectListDirtySeq = (state: RootState) =>
  state.chatAtendimentoConversations.listDirtySeq;
export const selectSegmentCounts = (state: RootState) =>
  state.chatAtendimentoConversations.segmentCounts;

const selectBotFlowsElegiveisEntries = (state: RootState) =>
  state.chatAtendimentoConversations.botFlowsElegiveis;

export const selectBotFlowsElegiveisEntry = (query: BotFlowsElegiveisQuery) => (state: RootState) =>
  state.chatAtendimentoConversations.botFlowsElegiveis[botFlowsElegiveisQueryKey(query)];

/** Junta as páginas 1..pageCount de uma busca para o select com "carregar mais". */
export const selectBotFlowsElegiveisPages = ({
  search,
  limit,
  pageCount,
}: BotFlowsElegiveisPagesQuery) =>
  createSelector([selectBotFlowsElegiveisEntries], (entries): BotFlowsElegiveisPages => {
    const pages = Array.from(
      { length: pageCount },
      (_, index) => entries[botFlowsElegiveisQueryKey({ page: index + 1, limit, search })],
    );
    const lastPage = pages[pages.length - 1];
    return {
      items: pages.flatMap((page) => page?.data?.items ?? []),
      hasMore: lastPage?.data?.hasMore ?? false,
      isLoading: !lastPage || lastPage.status === 'loading',
      isLoadingMore: pageCount > 1 && lastPage?.status === 'loading',
      errorMessage: pages.find((page) => page?.status === 'error')?.error ?? null,
    };
  });
